import React, { useState, useCallback, useRef } from 'react'
import { Checkbox, Tooltip } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { useDrag } from 'react-dnd'
import { Todo, Settings, DragItem } from '../types'
import { formatTime, PIXELS_PER_15_MIN } from '../utils/timeUtils'
import { mixWithWhite } from '../utils/colorUtils'

interface TodoItemProps {
  todo: Todo
  top: number
  height: number
  width: string
  left: string
  onEdit: (todo: Todo, element: HTMLElement) => void
  onDelete: (id: string) => void
  onResize?: (id: string, newHeight: number) => void
  onToggleComplete?: (id: string, completed: boolean) => void
  settings: Settings
  onResizingChange?: (resizing: boolean) => void
  onPreviewChange?: (y: number | null) => void
  isActive?: boolean
  onActivate?: (id: string) => void
  isMutating?: boolean
}

const TodoItem: React.FC<TodoItemProps> = ({
  todo,
  top,
  height,
  width,
  left,
  onEdit,
  onDelete,
  onResize,
  onToggleComplete,
  settings,
  onResizingChange,
  onPreviewChange,
  isActive,
  onActivate,
  isMutating
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeStart, setResizeStart] = useState<{ y: number; height: number; startMinutes: number; endMinutes: number } | null>(null)
  const [liveHeight, setLiveHeight] = useState<number | null>(null)
  const [liveEndTime, setLiveEndTime] = useState<string | null>(null)
  const onResizeRef = useRef(onResize)
  const revertStateRef = useRef<{ height: number; end_time: string } | null>(null)
  const dragStartOffsetYRef = useRef<number>(0)
  const titleRef = useRef<HTMLSpanElement | null>(null)
  const [titleOverflow, setTitleOverflow] = useState(false)

  // Update ref when onResize changes
  React.useEffect(() => {
    onResizeRef.current = onResize
  }, [onResize])

  const [{ isDragging: dragIsDragging }, drag] = useDrag(() => ({
    type: 'todo',
    item: () => ({ type: 'todo', id: todo.id, todo, offsetY: dragStartOffsetYRef.current } as DragItem),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  React.useEffect(() => {
    if (dragIsDragging !== isDragging) {
      setIsDragging(dragIsDragging)
    }
  }, [dragIsDragging, isDragging])

  // 计算标题是否溢出以决定是否显示 Tooltip
  const recomputeOverflow = useCallback(() => {
    const el = titleRef.current
    if (!el) return
    setTitleOverflow(el.scrollWidth > el.clientWidth)
  }, [])

  React.useEffect(() => {
    recomputeOverflow()
  }, [todo.title, isResizing, isDragging, recomputeOverflow])

  React.useEffect(() => {
    const onResize = () => recomputeOverflow()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [recomputeOverflow])

  const handleToggleComplete = (e: CheckboxChangeEvent) => {
    e.stopPropagation()
    if (onToggleComplete) {
      onToggleComplete(todo.id, !todo.completed)
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit(todo, e.currentTarget as HTMLElement)
  }

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    if (onResizingChange) onResizingChange(true)
    setResizeStart({
      y: e.clientY,
      height: height,
      startMinutes: parseInt(todo.start_time.split(':')[0]) * 60 + parseInt(todo.start_time.split(':')[1]),
      endMinutes: parseInt(todo.end_time.split(':')[0]) * 60 + parseInt(todo.end_time.split(':')[1])
    })
    revertStateRef.current = { height, end_time: todo.end_time }
  }

  // 实时更新高度与时间，不请求后端
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeStart) return
    const deltaY = e.clientY - resizeStart.y
    const newHeight = Math.max(PIXELS_PER_15_MIN, resizeStart.height + deltaY)
    setLiveHeight(newHeight)
    if (onPreviewChange) {
      onPreviewChange(top + newHeight)
    }
    const minutesDelta = Math.round((newHeight / PIXELS_PER_15_MIN) * 15)
    const newEndMinutes = resizeStart.startMinutes + minutesDelta
    const hours = Math.floor(newEndMinutes / 60)
    const mins = newEndMinutes % 60
    const newEndTime = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
    setLiveEndTime(newEndTime)
  }, [isResizing, resizeStart, top, onPreviewChange])

  const handleResizeEnd = useCallback(async () => {
    setIsResizing(false)
    if (onResizingChange) onResizingChange(false)
    setResizeStart(null)
    if (onPreviewChange) onPreviewChange(null)
    if (liveHeight && onResizeRef.current) {
      try {
        await onResizeRef.current(todo.id, liveHeight)
      } catch (err) {
        // 失败回滚UI
        setLiveHeight(null)
        setLiveEndTime(null)
      }
    }
    setLiveHeight(null)
    setLiveEndTime(null)
  }, [liveHeight, onResizeRef, todo.id])

  // Add global mouse event listeners for resize
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
      }
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  // Helper function to convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    if (!hex || !hex.startsWith('#')) return hex
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // 拉伸过程中不改变卡片实际高度，仅显示蓝色预览线
  const effectiveHeight = height
  // 根据开始/结束时间计算时长，超过35分钟则改为堆叠显示
  const parseMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
  }
  const displayEndTime = liveEndTime ?? todo.end_time
  const durationMinutes = Math.max(0, parseMinutes(displayEndTime) - parseMinutes(todo.start_time))
  const isCompact = durationMinutes <= 35
  const style: React.CSSProperties = {
    top: `${top}px`,
    height: `${effectiveHeight}px`,
    width,
    left,
    backgroundColor: todo.completed
      ? '#f5f5f5'
      : mixWithWhite(todo.color, 0.2),
    borderColor: todo.completed ? '#d9d9d9' : todo.color,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    color: todo.completed ? '#8c8c8c' : todo.color,
    opacity: isDragging ? 0.5 : 1,
    transform: isDragging ? 'rotate(2deg)' : 'none',
    zIndex: isDragging ? 1000 : isActive ? 200 : 10,
    cursor: isDragging ? 'grabbing' : isResizing ? 'ns-resize' : 'pointer',
    position: 'absolute',
    userSelect: isResizing ? 'none' : 'auto' // Prevent text selection during resize
  }

  return (
    <div
      ref={drag}
      className={`todo-item ${todo.completed ? 'completed' : ''} ${isDragging ? 'dragging' : ''} ${isCompact ? 'compact' : 'stacked'} ${isResizing ? 'resizing' : ''}`}
      style={style}
      onMouseDown={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        dragStartOffsetYRef.current = e.clientY - rect.top
      }}
      onClick={(e) => {
        // 单击仅设置激活（置顶），不打开编辑窗
        e.stopPropagation()
        if (!isResizing && onActivate) {
          onActivate(todo.id)
        }
      }}
      onDoubleClick={(e) => {
        if (isResizing) {
          e.stopPropagation()
          return
        }
        handleClick(e)
      }}
    >
      <Checkbox
        className="todo-checkbox"
        checked={todo.completed}
        onChange={handleToggleComplete}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="todo-content">
        <Tooltip title={titleOverflow ? todo.title : null} mouseEnterDelay={0.2}>
          <span ref={titleRef} className="todo-title" style={{ textDecoration: todo.completed ? 'line-through' : 'none' }}>
            {todo.title}
            {isMutating && (
              <span className="inline-spinner" aria-label="loading" />
            )}
          </span>
        </Tooltip>
        <span className="todo-time">
          {formatTime(todo.start_time)}-{formatTime(displayEndTime)}
        </span>
      </div>
      {/* Resize handle on bottom edge */}
      <div
        className={`todo-resize-handle ${isResizing ? 'active' : ''}`}
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '6px',
          cursor: 'ns-resize',
          backgroundColor: 'transparent',
          opacity: isResizing ? 1 : 0,
        }}
      />
    </div>
  )
}

export default TodoItem